import React from 'react'

import { Active, useDndMonitor, useDraggable } from '@dnd-kit/core'
import { Coordinates } from '@dnd-kit/utilities'
import { faArrowsUpDownLeftRight } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { alpha, Box, styled } from '@mui/material'
import { DraggableContainer } from './DraggableContainer'
import { Resizable } from 're-resizable'
import { ResizableArea } from './ResizableArea'
import { CornerResizeHandle } from './CornerResizeHandle'
import { BottomHandleStyles, BottomRightHandleStyles } from '../constants'
import { Dimensions } from '../ScreenMaskArea'
import { ScreenMaskState, updateScreenMaskDimensions } from '@app/storage/screenMask'
import { useSnapshot } from 'valtio'

const DRAGGABLE_CLEAR_AREA_ID = 'draggable-clear-area'

const MIN_CLEAR_WIDTH = 200
const MIN_CLEAR_HEIGHT = 50

type Props = {
  getParentElement: () => HTMLElement
  onResize: (data: Dimensions) => void
}

export const ClearContainer: React.FC<Props> = ({ getParentElement, onResize }) => {
  const { dimensions } = useSnapshot(ScreenMaskState)
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: DRAGGABLE_CLEAR_AREA_ID,
  })

  const onDragEnd = (active: Active, delta: Coordinates) => {
    if (active.id !== DRAGGABLE_CLEAR_AREA_ID) return

    updateScreenMaskDimensions({
      clearAreaX: dimensions.clearAreaX + delta.x,
      clearAreaY: dimensions.clearAreaY + delta.y,
    })
  }

  useDndMonitor({
    onDragEnd: ({ delta, active }) => onDragEnd(active, delta),
  })

  const onResizeStop = (elRef: HTMLElement) => {
    const { width, height } = elRef.getBoundingClientRect()

    onResize({ width, height, x: dimensions.clearAreaX, y: dimensions.clearAreaY })
  }

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
      }
    : {}

  const parentElement = getParentElement()
  // a11y: the clear area starts at x/y inside its parent, so the reachable max is the space left from there.
  const maxWidth = Math.max(dimensions.clearAreaWidth, (parentElement?.clientWidth ?? 0) - dimensions.clearAreaX)
  const maxHeight = Math.max(dimensions.clearAreaHeight, (parentElement?.clientHeight ?? 0) - dimensions.clearAreaY)

  return (
    <StyledContainer
      ref={setNodeRef}
      style={{
        top: dimensions.clearAreaY,
        left: dimensions.clearAreaX,
        ...style,
      }}
    >
      <DraggableContainer
        {...attributes}
        {...listeners}
        // a11y: application role keeps NVDA in key-passing mode so Space picks up and arrows drag; button role leaves NVDA in browse mode.
        role='application'
        aria-label='Drag visible area'
      >
        <FontAwesomeIcon icon={faArrowsUpDownLeftRight} />
      </DraggableContainer>
      <ClearArea
        size={{
          width: dimensions.clearAreaWidth,
          height: dimensions.clearAreaHeight,
        }}
        enable={{
          top: false,
          left: false,
          right: false,
          topRight: false,
          topLeft: false,
          bottomLeft: false,
          // a11y: list bottom before bottomRight so the height handle tabs before the corner (reading order).
          bottom: true,
          bottomRight: true,
        }}
        minHeight={MIN_CLEAR_HEIGHT}
        minWidth={MIN_CLEAR_WIDTH}
        handleComponent={{
          bottom: (
            <ResizableArea
              label='Resize visible area height'
              value={dimensions.clearAreaHeight}
              min={MIN_CLEAR_HEIGHT}
              max={maxHeight}
              onResize={delta =>
                updateScreenMaskDimensions({
                  clearAreaHeight: Math.max(dimensions.clearAreaHeight + delta, MIN_CLEAR_HEIGHT),
                })
              }
            />
          ),
          bottomRight: (
            <CornerResizeHandle
              label='Resize visible area width and height'
              width={dimensions.clearAreaWidth}
              height={dimensions.clearAreaHeight}
              minWidth={MIN_CLEAR_WIDTH}
              minHeight={MIN_CLEAR_HEIGHT}
              maxWidth={maxWidth}
              maxHeight={maxHeight}
              onResize={(wDelta, hDelta) =>
                updateScreenMaskDimensions({
                  clearAreaWidth: Math.max(dimensions.clearAreaWidth + wDelta, MIN_CLEAR_WIDTH),
                  clearAreaHeight: Math.max(dimensions.clearAreaHeight + hDelta, MIN_CLEAR_HEIGHT),
                })
              }
            />
          ),
        }}
        onResizeStop={(_e, _d, elRef) => onResizeStop(elRef)}
        handleStyles={{
          bottomRight: BottomRightHandleStyles,
          bottom: BottomHandleStyles,
        }}
        bounds={parentElement}
      />
    </StyledContainer>
  )
}

const StyledContainer = styled(Box)(() => ({
  position: 'absolute',
  display: 'flex',
  flexDirection: 'column',
  minWidth: '200px',
  minHeight: '50px',
  boxShadow: `0 0 0 2400px ${alpha('#636363', 0.75)}`,
}))

const ClearArea = styled(Resizable)(() => ({
  width: '200px',
  height: '50px',
  display: 'flex',
  flexDirection: 'column-reverse',
}))
